import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Col, Row, Skeleton, Space, Typography } from "antd";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../lib/firebase";
import { useAuth } from "../lib/auth";
import { markdownComponents } from "../components/MarkdownLink";
import {
  generateExecBriefing,
  getExecStats,
  todayKey,
  type ExecStatsResponse,
} from "../lib/api";
import PeriodChips, {
  buildPeriodOptions,
  fiscalYearFor,
} from "../components/exec/PeriodChips";
import StageFunnelCard from "../components/exec/StageFunnelCard";
import BacklogThemesCard from "../components/exec/BacklogThemesCard";
import ImpactSpotlightCard, {
  type SpotlightProject,
} from "../components/exec/ImpactSpotlightCard";
import RiskBandHeatmap from "../components/exec/RiskBandHeatmap";
import ExecPdfExportButton from "../components/exec/ExecPdfExportButton";

interface ExecSummaryDoc {
  date?: string;
  markdown?: string;
  generatedAt?: string;
}

export default function ExecDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodKey = searchParams.get("period") || `fy${fiscalYearFor()}`;

  const exportNodeRef = useRef<HTMLDivElement | null>(null);

  // Stats — refetched whenever period changes.
  const [stats, setStats] = useState<ExecStatsResponse | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);

  // Briefing
  const today = useMemo(() => todayKey(), []);
  const briefingDocId = `exec-${today}`;
  const [briefingDoc, setBriefingDoc] = useState<ExecSummaryDoc | null>(null);
  const [briefingReady, setBriefingReady] = useState(false);
  const [briefingStreaming, setBriefingStreaming] = useState("");
  const [refreshingBriefing, setRefreshingBriefing] = useState(false);

  // Spotlight — featured + recency fallback
  const [featured, setFeatured] = useState<SpotlightProject[]>([]);
  const [recent, setRecent] = useState<SpotlightProject[]>([]);

  const periodOptions = useMemo(() => buildPeriodOptions(), []);
  const currentPeriodLabel =
    periodOptions.find((opt) => opt.key === periodKey)?.label || periodKey;

  // --- effects ---------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    setStatsError(null);
    getExecStats(periodKey)
      .then((data) => {
        if (cancelled) return;
        setStats(data);
      })
      .catch((err) => {
        console.error("Failed to load exec stats:", err);
        if (!cancelled) setStatsError("Could not load stats right now.");
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [periodKey]);

  useEffect(() => {
    const ref = doc(db, "aiSummaries", briefingDocId);
    const unsubscribe = onSnapshot(ref, (snapshot) => {
      setBriefingReady(true);
      if (snapshot.exists()) {
        setBriefingDoc(snapshot.data() as ExecSummaryDoc);
      } else {
        setBriefingDoc(null);
      }
    });
    return () => unsubscribe();
  }, [briefingDocId]);

  useEffect(() => {
    const q = query(
      collection(db, "projects"),
      where("spotlightFeatured", "==", true),
      orderBy("impactLastReviewedAt", "desc"),
      limit(3)
    );
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const rows: SpotlightProject[] = [];
        snap.forEach((d) => rows.push({ id: d.id, ...(d.data() as Omit<SpotlightProject, "id">) }));
        setFeatured(rows);
      },
      (err) => {
        // Composite index may not exist yet; that's fine — fallback handles it.
        console.warn("Featured spotlight query failed:", err);
        setFeatured([]);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // Fallback pool: most-recently-touched projects that at least have an
    // impactHeadline. Client-side filter for simplicity; OAIP scale is fine.
    const q = query(collection(db, "projects"), orderBy("updatedAt", "desc"), limit(40));
    const unsubscribe = onSnapshot(q, (snap) => {
      const rows: SpotlightProject[] = [];
      snap.forEach((d) => {
        const data = d.data() as Record<string, unknown>;
        if (typeof data.impactHeadline === "string" && data.impactHeadline.trim()) {
          rows.push({ id: d.id, ...(data as Omit<SpotlightProject, "id">) });
        }
      });
      setRecent(rows);
    });
    return () => unsubscribe();
  }, []);

  const spotlightProjects = useMemo(() => {
    const seen = new Set<string>();
    const merged: SpotlightProject[] = [];
    [...featured, ...recent].forEach((proj) => {
      if (!seen.has(proj.id) && merged.length < 3) {
        seen.add(proj.id);
        merged.push(proj);
      }
    });
    return merged;
  }, [featured, recent]);

  // --- briefing handlers -----------------------------------------------

  const briefingIsToday = briefingDoc?.date === today;
  const briefingMarkdown = refreshingBriefing
    ? briefingStreaming
    : briefingIsToday
      ? briefingDoc?.markdown ?? ""
      : "";
  const showGenerateCta = briefingReady && !briefingIsToday && !refreshingBriefing;

  const handleRefreshBriefing = async () => {
    if (refreshingBriefing) return;
    setRefreshingBriefing(true);
    setBriefingStreaming("");
    try {
      const userName = user?.displayName || user?.email || "User";
      const userEmail = user?.email || "";
      await generateExecBriefing(
        {
          onDelta: (chunk) => setBriefingStreaming((prev) => `${prev}${chunk}`),
          onMeta: () => {
            // onSnapshot will pick up the new persisted doc.
          },
        },
        { userName, userEmail, timestamp: new Date().toISOString() }
      );
    } catch (err) {
      console.error("Failed to refresh exec briefing:", err);
    } finally {
      setRefreshingBriefing(false);
      setBriefingStreaming("");
    }
  };

  const handlePeriodChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("period", next);
    setSearchParams(params, { replace: true });
  };

  // --- render ---------------------------------------------------------

  const greeting = (() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  })();
  const name = user?.displayName?.split(" ")[0] || user?.email?.split("@")[0] || "there";

  const RefreshIcon = briefingIsToday ? ReloadOutlined : PlusOutlined;
  const briefingBusy = !briefingReady || refreshingBriefing;
  const briefingLabel = briefingIsToday ? "Refresh" : "Generate";

  return (
    <div className="apex-page" ref={exportNodeRef}>
      <div className="apex-page-header">
        <div>
          <p className="apex-eyebrow">Executive briefing &middot; {currentPeriodLabel}</p>
          <h1 className="apex-page-title" style={{ marginTop: 6 }}>
            {greeting}, {name}.
          </h1>
          <p className="apex-page-subtitle">
            Where the OAIP accelerator stands, what's queued up, and which projects are paying off.
          </p>
        </div>
        <Space>
          <Button
            type="text"
            icon={<RefreshIcon spin={refreshingBriefing && briefingIsToday} />}
            loading={refreshingBriefing}
            onClick={handleRefreshBriefing}
            disabled={briefingBusy}
          >
            {refreshingBriefing ? "Working…" : `${briefingLabel} briefing`}
          </Button>
          <ExecPdfExportButton getNode={() => exportNodeRef.current} />
        </Space>
      </div>

      <div style={{ marginBottom: 16 }}>
        <PeriodChips value={periodKey} onChange={handlePeriodChange} />
      </div>

      {/* Briefing */}
      <Card title="AI executive briefing" style={{ marginBottom: 16 }}>
        {!briefingReady ? (
          <Skeleton active paragraph={{ rows: 3 }} />
        ) : briefingMarkdown ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm, remarkBreaks]}
            components={markdownComponents}
          >
            {briefingMarkdown}
          </ReactMarkdown>
        ) : (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            {showGenerateCta
              ? "Today's briefing hasn't been generated yet — the nightly job runs at 6am ET, or click Generate to produce one now."
              : "Waiting for today's briefing…"}
          </Typography.Paragraph>
        )}
      </Card>

      {/* Pillar: Impact spotlight — leadership's most-valued section, so it
          leads, full-width, directly under the briefing. (OAIP-130) */}
      <div style={{ marginBottom: 16 }}>
        <ImpactSpotlightCard projects={spotlightProjects} />
      </div>

      {/* Current state: pipeline funnel + at-a-glance totals side by side. */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={14}>
          {statsLoading ? (
            <Card title="Pipeline by stage">
              <Skeleton active paragraph={{ rows: 6 }} />
            </Card>
          ) : statsError ? (
            <Card title="Pipeline by stage">
              <Typography.Text type="danger">{statsError}</Typography.Text>
            </Card>
          ) : (
            <StageFunnelCard
              funnel={stats?.funnel || []}
              totalShipped={stats?.totals?.shipped}
            />
          )}
        </Col>
        <Col xs={24} lg={10}>
          <Card title="At a glance" style={{ height: "100%" }}>
            {statsLoading || !stats ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                <StatRow label="Active projects" value={stats.totals.activeProjects} />
                <StatRow label="Delivered this period" value={stats.totals.shipped} />
                <StatRow label="Submitted intakes" value={stats.totals.submittedIntakes} />
                <StatRow label="Active vendor partners" value={stats.totals.activeVendors} />
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      {/* Supporting detail at the bottom: risk mix + backlog themes. */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          {statsLoading ? (
            <Card title="Active project risk mix">
              <Skeleton active paragraph={{ rows: 4 }} />
            </Card>
          ) : (
            <RiskBandHeatmap bands={stats?.riskBands || []} />
          )}
        </Col>
        <Col xs={24} lg={10}>
          <BacklogThemesCard />
        </Col>
      </Row>
    </div>
  );
}

function StatRow({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <Typography.Text style={{ color: "#56657a" }}>{label}</Typography.Text>
      <Typography.Text strong style={{ fontSize: 22, color: "#092940" }}>
        {value}
      </Typography.Text>
    </div>
  );
}
